/**
 * Application Constants
 * Centralized configuration for the AFF Portal
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  RETRY_ATTEMPTS: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS) || 3,
  RETRY_DELAY: parseInt(process.env.REACT_APP_API_RETRY_DELAY) || 1000,
};

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    REGISTER: process.env.REACT_APP_AUTH_REGISTER || '/api/auth/register/',
    LOGIN: process.env.REACT_APP_AUTH_LOGIN || '/api/auth/login/',
    LOGOUT: process.env.REACT_APP_AUTH_LOGOUT,
    VERIFY_EMAIL: process.env.REACT_APP_AUTH_VERIFY_EMAIL,
    PASSWORD_RESET_REQUEST: process.env.REACT_APP_AUTH_PASSWORD_RESET_REQUEST,
    PASSWORD_RESET: process.env.REACT_APP_AUTH_PASSWORD_RESET,
    PASSWORD_RESET_VALIDATE: process.env.REACT_APP_AUTH_PASSWORD_RESET_VALIDATE,
    TOKEN_REFRESH: process.env.REACT_APP_AUTH_TOKEN_REFRESH,
    PROFILE: process.env.REACT_APP_AUTH_PROFILE,

    // Admin Endpoints
    USERS: process.env.REACT_APP_AUTH_USERS,
    USERS_PENDING: process.env.REACT_APP_AUTH_USERS_PENDING,
    USERS_CREATE: process.env.REACT_APP_AUTH_USERS_CREATE,
    USERS_UPDATE: (userId) => `${process.env.REACT_APP_AUTH_USERS}${userId}/update/`,
    USERS_DELETE: (userId) => `${process.env.REACT_APP_AUTH_USERS}${userId}/delete/`,
    USERS_BULK_ACTION: process.env.REACT_APP_AUTH_USERS_BULK_ACTION,
    USERS_SEARCH: process.env.REACT_APP_AUTH_USERS_SEARCH,
    USER_APPROVE: (userId) => `${process.env.REACT_APP_AUTH_USERS}${userId}/approve/`,
    USER_BY_ID: (userId) => `${process.env.REACT_APP_AUTH_USERS}${userId}/`,
    DASHBOARD: process.env.REACT_APP_AUTH_DASHBOARD || '/api/auth/dashboard/',
  },
  MEMBERS: {
    // Member Applications (ViewSet)
    APPLICATIONS: process.env.REACT_APP_MEMBERS_APPLICATIONS,
    APPLICATION_BY_ID: (id) => `${process.env.REACT_APP_MEMBERS_APPLICATIONS}${id}/`,
    APPLICATION_REVIEW: (id) => `${process.env.REACT_APP_MEMBERS_APPLICATIONS}${id}/review/`,
    // Real-time duplicate-email check for the application form
    CHECK_EMAIL: process.env.REACT_APP_MEMBERS_CHECK_EMAIL || '/api/members/check-email/',

    // Members (ViewSet)
    MEMBERS: process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/',
    MEMBER_BY_ID: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/`,
    MEMBER_EMAIL_PREFERENCES: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/email-preferences/`,
    MEMBER_BULK_ACTION: process.env.REACT_APP_MEMBERS_BULK_ACTION || '/api/members/members/bulk-action/',

    // Search and Analytics Endpoints
    MEMBER_SEARCH: process.env.REACT_APP_MEMBERS_SEARCH,
    EXTERNAL_VIEW: process.env.REACT_APP_MEMBERS_EXTERNAL_VIEW,
    MEMBER_SYNC_STATUS: process.env.REACT_APP_MEMBERS_SYNC_STATUS,
    MEMBER_STATS: process.env.REACT_APP_MEMBERS_STATS,

    // MailChimp Lists (ViewSet)
    MAILCHIMP_LISTS: process.env.REACT_APP_MAILCHIMP_LISTS || '/api/members/mailchimp-lists/',
    MAILCHIMP_LIST_BY_ID: (id) => `${process.env.REACT_APP_MAILCHIMP_LISTS || '/api/members/mailchimp-lists/'}${id}/`,
    MAILCHIMP_SET_DEFAULT: (id) => `${process.env.REACT_APP_MAILCHIMP_LISTS || '/api/members/mailchimp-lists/'}${id}/set-default/`,
    MAILCHIMP_BULK_SYNC_MEMBERS: process.env.REACT_APP_MAILCHIMP_BULK_SYNC_MEMBERS || '/api/members/mailchimp-lists/bulk-sync-members/',

    // MailChimp Syncs (ViewSet)
    MAILCHIMP_SYNCS: process.env.REACT_APP_MAILCHIMP_SYNCS || '/api/members/mailchimp-syncs/',
    MAILCHIMP_SYNC_BY_ID: (id) => `${process.env.REACT_APP_MAILCHIMP_SYNCS || '/api/members/mailchimp-syncs/'}${id}/`,
    MAILCHIMP_SYNC_MEMBER: (id) => `${process.env.REACT_APP_MAILCHIMP_SYNCS || '/api/members/mailchimp-syncs/'}${id}/sync-member/`,

    // Financial Periods (ViewSet)
    FINANCIAL_PERIODS: process.env.REACT_APP_FINANCIAL_PERIODS,
    FINANCIAL_PERIOD_BY_ID: (id) => `${process.env.REACT_APP_FINANCIAL_PERIODS}${id}/`,

    // Distributors (ViewSet)
    DISTRIBUTORS: process.env.REACT_APP_DISTRIBUTORS,
    DISTRIBUTOR_BY_ID: (id) => `${process.env.REACT_APP_DISTRIBUTORS}${id}/`,

    // Invoices (ViewSet)
    INVOICES: process.env.REACT_APP_INVOICES || '/api/members/invoices/',
    INVOICE_BY_ID: (id) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}${id}/`,

    // Google Sheets Backup (ViewSet)
    BACKUP_SYNC_LOGS: process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/',
    BACKUP_SYNC_LOG_BY_ID: (id) => `${process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/'}${id}/`,

    // Google Sheets Integration Endpoints

    MEMBER_SYNC_TO_SHEETS: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/sync-to-sheets/`,
    BULK_SYNC_TO_SHEETS: process.env.REACT_APP_BULK_SYNC_TO_SHEETS || '/api/members/bulk-sync-to-sheets/',
    BACKUP_DASHBOARD: process.env.REACT_APP_BACKUP_DASHBOARD || '/api/members/backup/dashboard/',
    BACKUP_TRIGGER: process.env.REACT_APP_BACKUP_TRIGGER || '/api/members/backup/trigger-sync/',
    BACKUP_SYNC_LOG_DETAILS: (id) => `${process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/'}${id}/`,
    BACKUP_TEST_CONNECTION: process.env.REACT_APP_BACKUP_TEST_CONNECTION || '/api/members/backup/test-connection/',
    BACKUP_STATISTICS: process.env.REACT_APP_BACKUP_STATISTICS || '/api/members/backup/statistics/',
    BACKUP_SETTINGS_UPDATE: process.env.REACT_APP_BACKUP_SETTINGS_UPDATE || '/api/members/backup/settings/update/',
    BACKUP_IMPORT_FROM_SHEETS: process.env.REACT_APP_BACKUP_IMPORT_FROM_SHEETS || '/api/members/import-from-sheets/',


    // Xero Integration Endpoints
    INVOICE_CREATE: process.env.REACT_APP_INVOICES || '/api/members/invoices/',
    INVOICE_SEND_TO_XERO: (id) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}${id}/send-to-xero/`,
    INVOICE_SEND_EMAIL: (id) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}${id}/send-email/`,
    INVOICE_BULK_SEND: process.env.REACT_APP_INVOICE_BULK_SEND,
    INVOICE_BULK_CREATE: process.env.REACT_APP_INVOICE_BULK_CREATE,
    INVOICE_CREATE_AND_SEND_NEW_MEMBER: process.env.REACT_APP_INVOICE_CREATE_AND_SEND_NEW_MEMBER,
    INVOICE_BULK_ANNUAL_RENEWAL: process.env.REACT_APP_INVOICE_BULK_ANNUAL_RENEWAL,
    XERO_SYNC_STATUS: process.env.REACT_APP_XERO_SYNC_STATUS || '/api/members/xero/sync-status/',
    XERO_ACCOUNTS: process.env.REACT_APP_XERO_ACCOUNTS || '/api/members/xero/accounts/',
    XERO_TEST_CONNECTION: process.env.REACT_APP_XERO_TEST_CONNECTION || '/api/members/xero/test-connection/',
    XERO_SYNC_INVOICES: process.env.REACT_APP_XERO_SYNC_INVOICES || '/api/members/invoices/sync-all-to-xero/',
    XERO_SYNC_STATUS_FROM_XERO: '/api/members/invoices/sync-status-from-xero/',

    // Enhanced Invoice Management Endpoints
    INVOICE_PREVIEW: (id) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}${id}/preview/`,
    INVOICE_BULK_PREVIEW: process.env.REACT_APP_INVOICE_BULK_PREVIEW,
    INVOICE_TEMPLATES: process.env.REACT_APP_INVOICE_TEMPLATES,
    INVOICE_DASHBOARD: process.env.REACT_APP_INVOICE_DASHBOARD,
    INVOICE_VALIDATE: process.env.REACT_APP_INVOICE_VALIDATE,

    // Public (tokenised) invoice payment endpoints — no auth required
    INVOICE_PUBLIC_PAY: (token) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}pay/${token}/`,
    INVOICE_CREATE_CHECKOUT: (token) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}pay/${token}/create-checkout-session/`,
    INVOICE_CREATE_PAYPAL_ORDER: (token) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}pay/${token}/create-paypal-order/`,
    INVOICE_CAPTURE_PAYPAL_ORDER: (token) => `${process.env.REACT_APP_INVOICES || '/api/members/invoices/'}pay/${token}/capture-paypal-order/`,

    // MailChimp Integration Endpoints
    MAILCHIMP_DASHBOARD: process.env.REACT_APP_MAILCHIMP_DASHBOARD || '/api/members/mailchimp/dashboard/',
    MAILCHIMP_TEST_CONNECTION: process.env.REACT_APP_MAILCHIMP_TEST_CONNECTION || '/api/members/mailchimp/test-connection/',
    MAILCHIMP_CAMPAIGNS: process.env.REACT_APP_MAILCHIMP_CAMPAIGNS || '/api/members/mailchimp/campaigns/',
    MAILCHIMP_CAMPAIGN_BY_ID: (id) => `${process.env.REACT_APP_MAILCHIMP_CAMPAIGNS || '/api/members/mailchimp/campaigns/'}${id}/`,
    MAILCHIMP_CAMPAIGN_REPORTS: (id) => `${process.env.REACT_APP_MAILCHIMP_CAMPAIGNS || '/api/members/mailchimp/campaigns/'}${id}/reports/`,
    MAILCHIMP_CAMPAIGN_SEND: (id) => `${process.env.REACT_APP_MAILCHIMP_CAMPAIGNS || '/api/members/mailchimp/campaigns/'}${id}/send/`,
    MAILCHIMP_LIST_STATS: (id) => `${process.env.REACT_APP_MAILCHIMP_LISTS || '/api/members/mailchimp-lists/'}${id}/stats/`,
    MAILCHIMP_LIST_MEMBERS: (id) => `${process.env.REACT_APP_MAILCHIMP_LISTS || '/api/members/mailchimp-lists/'}${id}/members/`,
    MAILCHIMP_BULK_SYNC: process.env.REACT_APP_MAILCHIMP_BULK_SYNC || '/api/members/mailchimp/bulk-sync/',
    MAILCHIMP_SYNC_ALL: process.env.REACT_APP_MAILCHIMP_SYNC_ALL || '/api/members/mailchimp/sync-all/',
    MAILCHIMP_ACCOUNT_INFO: process.env.REACT_APP_MAILCHIMP_ACCOUNT_INFO || '/api/members/mailchimp/account-info/',
    MAILCHIMP_EMAIL_PREFERENCES: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/email-preferences/`,
    MAILCHIMP_MANUAL_SYNC: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/mailchimp-sync/`,

    // Bulk Invoice Operations Endpoints
    BULK_INVOICE_OPERATIONS: process.env.REACT_APP_BULK_INVOICE_OPERATIONS,
    BULK_INVOICE_OPERATIONS_CREATE: process.env.REACT_APP_BULK_INVOICE_OPERATIONS_CREATE,
    BULK_INVOICE_OPERATIONS_PREVIEW: process.env.REACT_APP_BULK_INVOICE_OPERATIONS_PREVIEW,
    BULK_INVOICE_OPERATIONS_DASHBOARD: process.env.REACT_APP_BULK_INVOICE_OPERATIONS_DASHBOARD,
    BULK_INVOICE_OPERATIONS_CREATE_ANNUAL_RENEWAL: process.env.REACT_APP_BULK_INVOICE_OPERATIONS_CREATE_ANNUAL_RENEWAL,
    BULK_INVOICE_OPERATION_BY_ID: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/`,
    BULK_INVOICE_OPERATION_APPROVE: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/approve/`,
    BULK_INVOICE_OPERATION_START: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/start/`,
    BULK_INVOICE_OPERATION_CANCEL: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/cancel/`,
    BULK_INVOICE_OPERATION_STATUS: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/status/`,
    BULK_INVOICE_OPERATION_MEMBERS: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/members/`,
    BULK_INVOICE_OPERATION_LOGS: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/logs/`,
    BULK_INVOICE_OPERATION_SUMMARY: (id) => `${process.env.REACT_APP_BULK_INVOICE_OPERATIONS}${id}/summary/`,
    BULK_INVOICE_OPERATION_ACTION: process.env.REACT_APP_BULK_INVOICE_OPERATION_ACTION,
  },
  GOOGLE_SHEETS: {
    // Google Sheets Integration Endpoints
    MEMBER_SYNC_TO_SHEETS: (id) => `${process.env.REACT_APP_MEMBERS_MEMBERS || '/api/members/members/'}${id}/sync-to-sheets/`,
    BULK_SYNC_TO_SHEETS: process.env.REACT_APP_BULK_SYNC_TO_SHEETS || '/api/members/bulk-sync-to-sheets/',

    // Backup Management Endpoints
    BACKUP_DASHBOARD: process.env.REACT_APP_BACKUP_DASHBOARD || '/api/members/backup/dashboard/',
    BACKUP_TRIGGER: process.env.REACT_APP_BACKUP_TRIGGER || '/api/members/backup/trigger-sync/',
    BACKUP_SYNC_LOG_DETAILS: (id) => `${process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/'}${id}/`,
    BACKUP_STATISTICS: process.env.REACT_APP_BACKUP_STATISTICS || '/api/members/backup/statistics/',
    BACKUP_SETTINGS_UPDATE: process.env.REACT_APP_BACKUP_SETTINGS_UPDATE || '/api/members/backup/settings/update/',
    BACKUP_IMPORT_FROM_SHEETS: process.env.REACT_APP_BACKUP_IMPORT_FROM_SHEETS || '/api/members/import-from-sheets/',

    // Sync Logs Endpoints
    BACKUP_SYNC_LOGS: process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/',
    BACKUP_TEST_CONNECTION: process.env.REACT_APP_BACKUP_TEST_CONNECTION || '/api/members/backup/test-connection/',
    BACKUP_SYNC_LOG_BY_ID: (id) => `${process.env.REACT_APP_BACKUP_SYNC_LOGS || '/api/members/backup-sync-logs/'}${id}/`,


  },

};

// User Roles
export const USER_ROLES = {
  SUPERUSER: 'Superuser',
  STAFF: 'Staff',
  REGULAR: 'Regular User',
};

// User Status
export const USER_STATUS = {
  EMAIL_VERIFIED: 'email_verified',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

// Approval Status
export const APPROVAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Member Status
export const MEMBER_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  PENDING: 'pending',
};

// Application Status
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
};


// MailChimp Integration Status
export const MAILCHIMP_STATUS = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
  SYNCING: 'syncing',
};

// MailChimp Campaign Status
export const MAILCHIMP_CAMPAIGN_STATUS = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  SENDING: 'sending',
  SENT: 'sent',
  FAILED: 'failed',
};

// Financial Period Status
export const FINANCIAL_PERIOD_STATUS = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  INACTIVE: 'inactive',
};

// Distributor Specializations
export const DISTRIBUTOR_SPECIALIZATIONS = {
  INDEPENDENT_FILMS: 'independent_films',
  DOCUMENTARIES: 'documentaries',
  SHORT_FILMS: 'short_films',
  ANIMATION: 'animation',
  EXPERIMENTAL: 'experimental',
  CLASSIC_FILMS: 'classic_films',
  INTERNATIONAL: 'international',
  LOCAL: 'local',
};

// Google Sheets Sync Status
export const GOOGLE_SHEETS_SYNC_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Enhanced Invoice Status
export const ENHANCED_INVOICE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  SENT: 'sent',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
};

// Bulk Invoice Operation Status
export const BULK_INVOICE_OPERATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Bulk Invoice Operation Types
export const BULK_INVOICE_OPERATION_TYPES = {
  ANNUAL_RENEWAL: 'annual_renewal',
  MEMBERSHIP_FEE: 'membership_fee',
  EVENT_FEE: 'event_fee',
  CUSTOM: 'custom',
};

// Member Screening Formats
export const SCREENING_FORMATS = {
  '16mm Film': '16mm',
  '35mm Film': '35mm',
  'DVD': 'dvd',
  'Blu-ray': 'blu_ray',
  '4K': '4k',
  'Digital': 'digital',
  'Other': 'other',
};

// Publication Preferences
export const PUBLICATION_PREFERENCES = {
  NEWSLETTER: 'newsletter',
  CATALOG: 'catalog',
  BOTH: 'both',
  NONE: 'none',
};

// Payment Status
export const PAYMENT_STATUS = {
  CURRENT: 'current',
  OVERDUE: 'overdue',
  PAID: 'paid',
  PENDING: 'pending',
};


// Invoice Status
export const INVOICE_STATUS = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
  VOIDED: 'Voided',
};

// Invoice Types
export const INVOICE_TYPES = {
  NEW_MEMBER: 'New Member',
  RENEWAL: 'Renewal',
  LATE_FEE: 'Late Fee',
  OTHER: 'Other',
};

// Xero Sync Status
export const XERO_SYNC_STATUS = {
  NOT_SYNCED: 'Not Synced',
  PENDING: 'Pending',
  SYNCED: 'Synced',
  FAILED: 'Failed',
};

// Bulk Operation Status
export const BULK_OPERATION_STATUS = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

// Bulk Operation Types
export const BULK_OPERATION_TYPES = {
  ANNUAL_RENEWAL: 'Annual Renewal',
  BULK_CREATE: 'Bulk Create',
  BULK_SEND: 'Bulk Send',
  BULK_REMINDER: 'Bulk Reminder',
};

// Payment Methods
export const PAYMENT_METHODS = {
  BANK_TRANSFER: 'Bank Transfer',
  CREDIT_CARD: 'Credit Card',
  CHECK: 'Check',
  CASH: 'Cash',
  OTHER: 'Other',
};

// Integration Status
export const INTEGRATION_STATUS = {
  CONNECTED: 'Connected',
  DISCONNECTED: 'Disconnected',
  ERROR: 'Error',
  SYNCING: 'Syncing',
  PENDING: 'Pending',
};

// Integration Types
export const INTEGRATION_TYPES = {
  MAILCHIMP: 'mailchimp',
  GOOGLE_SHEETS: 'google_sheets',
  XERO: 'xero',
};

// Sync Status
export const SYNC_STATUS = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  IN_PROGRESS: 'In Progress',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
};

// Error Severity
export const ERROR_SEVERITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};



// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE) || 10,
  MAX_PAGE_SIZE: parseInt(process.env.REACT_APP_MAX_PAGE_SIZE) || 100,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};


// Account Security
export const SECURITY_CONFIG = {
  MAX_LOGIN_ATTEMPTS: parseInt(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS) || 5,
  LOCKOUT_DURATION: parseInt(process.env.REACT_APP_LOCKOUT_DURATION) || 15 * 60 * 1000, // 15 minutes
  PASSWORD_MIN_LENGTH: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH) || 8,
  PASSWORD_REQUIREMENTS: {
    minLength: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH) || 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true
  },
};

// Form Validation
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN_LENGTH: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH) || 8,
  PHONE_REGEX: /^[+]?[1-9][\d]{0,15}$/,
  MEMBER_NUMBER_REGEX: /^AFF-\d{4}-\d{3}$/,

  // Financial Validation
  MAX_AMOUNT: parseInt(process.env.REACT_APP_MAX_AMOUNT) || 1000000, // $1,000,000
  MIN_DESCRIPTION_LENGTH: parseInt(process.env.REACT_APP_MIN_DESCRIPTION_LENGTH) || 10,
  MAX_DESCRIPTION_LENGTH: parseInt(process.env.REACT_APP_MAX_DESCRIPTION_LENGTH) || 500,
  MIN_NAME_LENGTH: parseInt(process.env.REACT_APP_MIN_NAME_LENGTH) || 3,
  MAX_NAME_LENGTH: parseInt(process.env.REACT_APP_MAX_NAME_LENGTH) || 100,
  MAX_NOTES_LENGTH: parseInt(process.env.REACT_APP_MAX_NOTES_LENGTH) || 1000,
  MIN_INVOICE_AMOUNT: parseFloat(process.env.REACT_APP_MIN_INVOICE_AMOUNT) || 0.01,
  MAX_INVOICE_AMOUNT: parseInt(process.env.REACT_APP_MAX_INVOICE_AMOUNT) || 1000000,
};

// UI Constants
export const UI_CONFIG = {
  DEBOUNCE_DELAY: parseInt(process.env.REACT_APP_DEBOUNCE_DELAY) || 300,
  ANIMATION_DURATION: parseInt(process.env.REACT_APP_ANIMATION_DURATION) || 300,
  TOAST_DURATION: parseInt(process.env.REACT_APP_TOAST_DURATION) || 5000,
  LOADING_DELAY: parseInt(process.env.REACT_APP_LOADING_DELAY) || 500,
};

// Feature Flags
export const FEATURES = {
  ENABLE_DEBUG_MODE: process.env.REACT_APP_ENABLE_DEBUG_MODE === 'true' || process.env.NODE_ENV === 'development',
  ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
  ENABLE_MOCK_DATA: process.env.REACT_APP_ENABLE_MOCK_DATA === 'true',
  ENABLE_CONSOLE_LOGS: process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true',
  ENABLE_ERROR_REPORTING: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true',
  ENABLE_PERFORMANCE_MONITORING: process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true',
};


// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',

  // Financial Error Messages
  INVOICE_CREATE_FAILED: 'Failed to create invoice.',
  INVOICE_UPDATE_FAILED: 'Failed to update invoice.',
  INVOICE_DELETE_FAILED: 'Failed to delete invoice.',
  INVOICE_SEND_FAILED: 'Failed to send invoice.',
  INVOICE_PAYMENT_FAILED: 'Failed to process payment.',
  INVOICE_CANCEL_FAILED: 'Failed to cancel invoice.',
  INVOICE_VOID_FAILED: 'Failed to void invoice.',

  PERIOD_CREATE_FAILED: 'Failed to create financial period.',
  PERIOD_UPDATE_FAILED: 'Failed to update financial period.',
  PERIOD_DELETE_FAILED: 'Failed to delete financial period.',
  PERIOD_ACTIVATE_FAILED: 'Failed to activate financial period.',
  PERIOD_OVERLAP: 'Financial period overlaps with existing period.',

  BULK_OPERATION_CREATE_FAILED: 'Failed to create bulk operation.',
  BULK_OPERATION_START_FAILED: 'Failed to start bulk operation.',
  BULK_OPERATION_FAILED: 'Bulk operation failed.',

  XERO_SYNC_FAILED: 'Failed to sync invoices to Xero.',
  XERO_CONNECTION_FAILED: 'Xero connection test failed.',


  PAYMENT_ADD_FAILED: 'Failed to add payment.',
  REMINDER_SEND_FAILED: 'Failed to send reminder.',
  CREDIT_LIMIT_EXCEEDED: 'Invoice amount exceeds member credit limit.',
  INVALID_STATUS_TRANSITION: 'Invalid status transition.',
  XERO_SYNC_PREREQUISITES_NOT_MET: 'Xero sync prerequisites not met.',

  // Integration Error Messages
  INTEGRATION_CONNECTION_FAILED: 'Failed to connect to integration.',
  INTEGRATION_SYNC_FAILED: 'Integration sync failed.',
  INTEGRATION_CONFIGURATION_ERROR: 'Integration configuration error.',
  INTEGRATION_AUTHENTICATION_FAILED: 'Integration authentication failed.',
  INTEGRATION_RATE_LIMIT_EXCEEDED: 'Integration rate limit exceeded.',
  INTEGRATION_SERVICE_UNAVAILABLE: 'Integration service unavailable.',
  INTEGRATION_DATA_VALIDATION_FAILED: 'Integration data validation failed.',
  INTEGRATION_RETRY_FAILED: 'Integration retry failed.',
  INTEGRATION_PERMISSION_DENIED: 'Integration permission denied.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully.',
  DELETED: 'Item deleted successfully.',
  CREATED: 'Item created successfully.',
  UPDATED: 'Item updated successfully.',
  EMAIL_SENT: 'Email sent successfully.',
  VERIFIED: 'Email verified successfully.',

  // Financial Success Messages
  INVOICE_CREATED: 'Invoice created successfully.',
  INVOICE_UPDATED: 'Invoice updated successfully.',
  INVOICE_DELETED: 'Invoice deleted successfully.',
  INVOICE_SENT: 'Invoice sent successfully.',
  INVOICE_PAID: 'Invoice marked as paid successfully.',
  INVOICE_CANCELLED: 'Invoice cancelled successfully.',
  INVOICE_VOIDED: 'Invoice voided successfully.',

  PERIOD_CREATED: 'Financial period created successfully.',
  PERIOD_UPDATED: 'Financial period updated successfully.',
  PERIOD_DELETED: 'Financial period deleted successfully.',
  PERIOD_ACTIVATED: 'Financial period activated successfully.',

  BULK_OPERATION_CREATED: 'Bulk operation created successfully.',
  BULK_OPERATION_STARTED: 'Bulk operation started successfully.',
  BULK_OPERATION_COMPLETED: 'Bulk operation completed successfully.',

  XERO_SYNC_SUCCESS: 'Invoices synced to Xero successfully.',
  XERO_CONNECTION_SUCCESS: 'Xero connection test successful.',


  PAYMENT_ADDED: 'Payment added successfully.',
  REMINDER_SENT: 'Reminder sent successfully.',

  // Integration Success Messages
  INTEGRATION_CONNECTED: 'Integration connected successfully.',
  INTEGRATION_SYNC_SUCCESS: 'Integration sync completed successfully.',
  INTEGRATION_CONFIGURATION_SAVED: 'Integration configuration saved successfully.',
  INTEGRATION_DISCONNECTED: 'Integration disconnected successfully.',
  INTEGRATION_RETRY_SUCCESS: 'Integration retry completed successfully.',
};
